/**
 * Cable Shaft Exit Manager
 *
 * Kablo bacası çıkış noktalarını yönetir:
 * - Çapraz çizgi üzerinde eşit aralıklı yeşil noktalar
 * - Kablo girişinde/çıkışında dinamik nokta ekleme
 * - Mevcut kabloların vertex güncelleme
 * - Hover etkileşimi
 */

import * as THREE from 'three';

// Çıkış noktası yarıçapı
const EXIT_POINT_RADIUS = 2;
const EXIT_POINT_COLOR = 0x00ff00; // Yeşil

/**
 * Çapraz çizgi üzerindeki bir noktayı hesaplar
 * @param {Object} start - Başlangıç noktası {x, y}
 * @param {Object} end - Bitiş noktası {x, y}
 * @param {number} t - 0-1 arası parametre (0=start, 1=end)
 * @returns {THREE.Vector3}
 */
export function getPointOnDiagonal(start, end, t) {
    return new THREE.Vector3(
        start.x + (end.x - start.x) * t,
        start.y + (end.y - start.y) * t,
        0.15 // Slightly above the shaft
    );
}

/**
 * Çapraz çizgiyi oluşturur (kablo bacasının köşegen çizgisi)
 * @param {Object} corner1 - İlk köşe {x, y}
 * @param {Object} corner2 - İkinci köşe (çapraz köşe) {x, y}
 * @param {string} shaftId - Kablo bacası ID
 * @returns {THREE.Line}
 */
export function createDiagonalLine(corner1, corner2, shaftId) {
    const points = [
        new THREE.Vector3(corner1.x, corner1.y, 0.1),
        new THREE.Vector3(corner2.x, corner2.y, 0.1)
    ];

    const geometry = new THREE.BufferGeometry().setFromPoints(points);
    const material = new THREE.LineBasicMaterial({
        color: 0x00ff00, // Yeşil
        linewidth: 2
    });

    const line = new THREE.Line(geometry, material);
    line.name = 'shaft-diagonal-line';
    line.userData = {
        type: 'shaftDiagonalLine',
        shaftId: shaftId,
        start: { x: corner1.x, y: corner1.y },
        end: { x: corner2.x, y: corner2.y }
    };

    return line;
}

/**
 * Çıkış noktası (yeşil daire) oluşturur
 * @param {THREE.Vector3} position - Nokta pozisyonu
 * @param {string} shaftId - Kablo bacası ID
 * @param {number} exitIndex - Çıkış indeksi
 * @param {boolean} isOccupied - Nokta dolu mu?
 * @param {string|null} wireId - Bağlı wire ID (varsa)
 * @returns {THREE.Mesh}
 */
export function createExitPoint(position, shaftId, exitIndex, isOccupied = false, wireId = null) {
    const geometry = new THREE.CircleGeometry(EXIT_POINT_RADIUS, 16);
    const material = new THREE.MeshBasicMaterial({
        color: isOccupied ? 0xff6600 : EXIT_POINT_COLOR, // Dolu ise turuncu, boş ise yeşil
        side: THREE.DoubleSide,
        transparent: true,
        opacity: 0.8
    });

    const circle = new THREE.Mesh(geometry, material);
    circle.position.copy(position);
    circle.name = `shaft-exit-point-${exitIndex}`;
    circle.userData = {
        type: 'shaftExitPoint',
        shaftId: shaftId,
        exitIndex: exitIndex,
        isExitPoint: true,
        isOccupied: isOccupied,
        wireId: wireId,
        center: { x: position.x, y: position.y },
        radius: EXIT_POINT_RADIUS
    };

    return circle;
}

/**
 * Kablo bacasındaki mevcut çıkış noktalarını bulur
 * @param {THREE.Scene} scene
 * @param {string} shaftId
 * @returns {THREE.Mesh[]}
 */
export function findShaftExitPoints(scene, shaftId) {
    const exitPoints = [];

    scene.traverse((obj) => {
        if (obj.userData?.type === 'shaftExitPoint' && obj.userData?.shaftId === shaftId) {
            exitPoints.push(obj);
        }
    });

    return exitPoints.sort((a, b) => a.userData.exitIndex - b.userData.exitIndex);
}

/**
 * Kablo bacasındaki çapraz çizgiyi bulur
 * @param {THREE.Scene} scene
 * @param {string} shaftId
 * @returns {THREE.Line|null}
 */
export function findShaftDiagonalLine(scene, shaftId) {
    let diagonalLine = null;

    scene.traverse((obj) => {
        if (obj.userData?.type === 'shaftDiagonalLine' && obj.userData?.shaftId === shaftId) {
            diagonalLine = obj;
        }
    });

    return diagonalLine;
}

/**
 * Çıkış noktalarını günceller (yeniden hesaplar ve yerleştirir)
 * @param {THREE.Scene} scene
 * @param {string} shaftId
 * @param {number} totalExits - Toplam çıkış sayısı
 * @param {Array} occupiedExits - Dolu çıkışların bilgisi [{exitIndex, wireId, direction}]
 */
export function updateShaftExitPoints(scene, shaftId, totalExits, occupiedExits = []) {
    // Çapraz çizgiyi bul
    const diagonalLine = findShaftDiagonalLine(scene, shaftId);
    if (!diagonalLine) {
        console.warn('[CableShaftExitManager] Diagonal line not found for shaft:', shaftId);
        return;
    }

    const { start, end } = diagonalLine.userData;

    // Mevcut çıkış noktalarını temizle
    const existingPoints = findShaftExitPoints(scene, shaftId);
    existingPoints.forEach(point => {
        if (point.parent) point.parent.remove(point);
        else scene.remove(point);
        point.geometry.dispose();
        point.material.dispose();
    });

    // Yeni çıkış noktaları oluştur
    // Çizgiyi (totalExits + 1) parçaya böl, her parçanın ortasına nokta koy
    for (let i = 0; i < totalExits; i++) {
        // t = (i + 1) / (totalExits + 1) formülü ile eşit aralıklı noktalar
        const t = (i + 1) / (totalExits + 1);
        const position = getPointOnDiagonal(start, end, t);

        // Bu çıkış dolu mu?
        const occupiedInfo = occupiedExits.find(oe => oe.exitIndex === i);
        const isOccupied = !!occupiedInfo;
        const wireId = occupiedInfo?.wireId || null;

        const exitPoint = createExitPoint(position, shaftId, i, isOccupied, wireId);
        scene.add(exitPoint);
    }


}

/**
 * Yeni bir çıkış noktası ekler (kablo girdiğinde/çıktığında)
 * @param {THREE.Scene} scene
 * @param {string} shaftId
 * @param {string} wireId - Yeni wire ID
 * @param {string} direction - 'in' veya 'out'
 * @returns {Object} - Yeni çıkış noktası pozisyonu {x, y, exitIndex}
 */
export function addShaftExitPoint(scene, shaftId, wireId, direction = 'in') {
    // Mevcut çıkışları bul
    const existingPoints = findShaftExitPoints(scene, shaftId);
    const currentCount = existingPoints.length;

    // Dolu çıkışların bilgisini al
    const occupiedExits = existingPoints
        .filter(p => p.userData.isOccupied)
        .map(p => ({
            exitIndex: p.userData.exitIndex,
            wireId: p.userData.wireId,
            direction: p.userData.direction
        }));

    // Yeni çıkışı ekle
    const newExitIndex = currentCount; // Yeni çıkış sona eklenir
    occupiedExits.push({
        exitIndex: newExitIndex,
        wireId: wireId,
        direction: direction
    });

    // Çıkış noktalarını güncelle (bir fazla ile)
    updateShaftExitPoints(scene, shaftId, currentCount + 1, occupiedExits);

    // Yeni çıkış noktasının pozisyonunu döndür
    const diagonalLine = findShaftDiagonalLine(scene, shaftId);
    if (diagonalLine) {
        const { start, end } = diagonalLine.userData;
        const t = (newExitIndex + 1) / (currentCount + 2);
        const position = getPointOnDiagonal(start, end, t);
        return { x: position.x, y: position.y, exitIndex: newExitIndex };
    }

    return null;
}

/**
 * Bir çıkış noktasını serbest bırakır
 * @param {THREE.Scene} scene
 * @param {string} shaftId
 * @param {number} exitIndex
 */
export function releaseShaftExitPoint(scene, shaftId, exitIndex) {
    const exitPoints = findShaftExitPoints(scene, shaftId);
    const targetPoint = exitPoints.find(p => p.userData.exitIndex === exitIndex);

    if (targetPoint) {
        targetPoint.userData.isOccupied = false;
        targetPoint.userData.wireId = null;
        targetPoint.material.color.setHex(EXIT_POINT_COLOR);
    }
}

/**
 * Boş bir çıkış noktası bulur
 * @param {THREE.Scene} scene
 * @param {string} shaftId
 * @returns {Object|null} - Boş çıkış noktası pozisyonu {x, y, exitIndex}
 */
export function findAvailableExitPoint(scene, shaftId) {
    const exitPoints = findShaftExitPoints(scene, shaftId);
    const availablePoint = exitPoints.find(p => !p.userData.isOccupied);

    if (availablePoint) {
        return {
            x: availablePoint.position.x,
            y: availablePoint.position.y,
            exitIndex: availablePoint.userData.exitIndex
        };
    }

    return null;
}

/**
 * Wire'ın kablo bacası içindeki vertex'lerini günceller
 * @param {THREE.Scene} scene
 * @param {string} wireId
 * @param {THREE.Vector3} newExitPosition - Yeni çıkış noktası pozisyonu
 * @param {string} connectionType - 'start' veya 'end'
 */
export function updateWireVerticesForShaft(scene, wireId, newExitPosition, connectionType = 'end') {
    let wireLine = null;

    scene.traverse((obj) => {
        if (obj.userData?.id === wireId || obj.userData?.wireID === wireId) {
            wireLine = obj;
        }
    });

    if (!wireLine || !wireLine.geometry) {
        console.warn('[CableShaftExitManager] Wire not found:', wireId);
        return;
    }

    const positionAttr = wireLine.geometry.attributes.position;
    if (!positionAttr) return;

    const numVertices = positionAttr.count;

    if (connectionType === 'end' && numVertices >= 2) {
        // Son iki vertex'i güncelle
        positionAttr.setXYZ(numVertices - 1, newExitPosition.x, newExitPosition.y, newExitPosition.z || 0);

        // İkinci sondan önceki vertex'i de kaydır (ok sembolü için)
        const secondLastX = positionAttr.getX(numVertices - 2);
        const secondLastY = positionAttr.getY(numVertices - 2);
        const lastX = positionAttr.getX(numVertices - 1);
        const lastY = positionAttr.getY(numVertices - 1);

        // Yön vektörünü koru, sadece son noktayı değiştir
        const dx = lastX - secondLastX;
        const dy = lastY - secondLastY;
        const len = Math.sqrt(dx * dx + dy * dy);

        if (len > 0) {
            const normalizedDx = dx / len;
            const normalizedDy = dy / len;

            // İkinci son vertex'i yeni son noktadan geriye doğru yerleştir
            const offset = Math.min(len, 10); // Maksimum 10 birim
            positionAttr.setXYZ(
                numVertices - 2,
                newExitPosition.x - normalizedDx * offset,
                newExitPosition.y - normalizedDy * offset,
                0
            );
        }

    } else if (connectionType === 'start' && numVertices >= 2) {
        // İlk iki vertex'i güncelle
        positionAttr.setXYZ(0, newExitPosition.x, newExitPosition.y, newExitPosition.z || 0);

        const firstX = positionAttr.getX(0);
        const firstY = positionAttr.getY(0);
        const secondX = positionAttr.getX(1);
        const secondY = positionAttr.getY(1);

        const dx = secondX - firstX;
        const dy = secondY - firstY;
        const len = Math.sqrt(dx * dx + dy * dy);

        if (len > 0) {
            const normalizedDx = dx / len;
            const normalizedDy = dy / len;

            const offset = Math.min(len, 10);
            positionAttr.setXYZ(
                1,
                newExitPosition.x + normalizedDx * offset,
                newExitPosition.y + normalizedDy * offset,
                0
            );
        }
    }

    positionAttr.needsUpdate = true;
    wireLine.geometry.computeBoundingSphere();

    // userData.vertices'i de güncelle
    if (wireLine.userData?.vertices) {
        if (connectionType === 'end') {
            wireLine.userData.vertices[wireLine.userData.vertices.length - 1] = {
                x: newExitPosition.x,
                y: newExitPosition.y,
                z: 0
            };
        } else {
            wireLine.userData.vertices[0] = {
                x: newExitPosition.x,
                y: newExitPosition.y,
                z: 0
            };
        }
    }
}

/**
 * Tüm wire'ların çıkış noktalarını yeniden hesaplar ve günceller
 * @param {THREE.Scene} scene
 * @param {string} shaftId
 */
export function redistributeShaftWires(scene, shaftId) {
    const exitPoints = findShaftExitPoints(scene, shaftId);

    exitPoints.forEach(exitPoint => {
        if (exitPoint.userData.isOccupied && exitPoint.userData.wireId) {
            updateWireVerticesForShaft(
                scene,
                exitPoint.userData.wireId,
                exitPoint.position,
                exitPoint.userData.direction === 'in' ? 'end' : 'start'
            );
        }
    });
}

/**
 * Hover efekti için çıkış noktalarını kaydırır
 * @param {THREE.Scene} scene
 * @param {string} shaftId
 * @param {number} hoveredExitIndex - Hover edilen çıkış indeksi
 */
export function shiftExitPointsForHover(scene, shaftId, hoveredExitIndex) {
    const exitPoints = findShaftExitPoints(scene, shaftId);

    // Hovered exit point'in pozisyonunu boşalt
    const hoveredPoint = exitPoints.find(p => p.userData.exitIndex === hoveredExitIndex);
    if (!hoveredPoint || !hoveredPoint.userData.isOccupied) return;

    // Boş bir nokta bul (hovered'dan farklı)
    const emptyPoint = exitPoints.find(p => !p.userData.isOccupied && p.userData.exitIndex !== hoveredExitIndex);
    if (!emptyPoint) return;

    // Wire'ları kaydır
    const wireId = hoveredPoint.userData.wireId;
    const direction = hoveredPoint.userData.direction;

    // Hovered point'i boşalt
    hoveredPoint.userData.isOccupied = false;
    hoveredPoint.userData.wireId = null;
    hoveredPoint.material.color.setHex(EXIT_POINT_COLOR);

    // Empty point'e taşı
    emptyPoint.userData.isOccupied = true;
    emptyPoint.userData.wireId = wireId;
    emptyPoint.userData.direction = direction;
    emptyPoint.material.color.setHex(0xff6600);

    // Wire vertex'lerini güncelle
    updateWireVerticesForShaft(
        scene,
        wireId,
        emptyPoint.position,
        direction === 'in' ? 'end' : 'start'
    );
}

/**
 * Kablo bacası için snap noktaları oluşturur
 * @param {THREE.Scene} scene
 * @param {string} shaftId
 * @returns {Array} - Snap noktaları [{point, type, shaftId, exitIndex}]
 */
export function getShaftSnapPoints(scene, shaftId) {
    const exitPoints = findShaftExitPoints(scene, shaftId);

    return exitPoints
        .filter(p => !p.userData.isOccupied) // Sadece boş noktalar
        .map(p => ({
            point: new THREE.Vector3(p.position.x, p.position.y, 0),
            type: 'shaftExit',
            shaftId: shaftId,
            exitIndex: p.userData.exitIndex
        }));
}
